import { type DragEndEvent, useDndMonitor, useDroppable } from '@dnd-kit/core';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Grid, Typography } from '@mui/material';
import { useAtom } from 'jotai';
import { type Dispatch, type SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { importModalAtom } from '@/atoms/import';
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { BooleanFilter } from '@/utils/helpers';
import { DroppableCharacters } from './ImportScript.styles';

const Droppable = ({ id }: { id: string }) => {
    const { setNodeRef } = useDroppable({ id });

    return <div ref={setNodeRef} className="flex flex-wrap w-full h-full absolute"></div>;
};

const CharacterItem = ({ name, onClick }: { name: string; onClick: () => void }) => {
    return (
        <Box
            sx={{
                padding: '2px 6px',
                borderRadius: '6px',
                display: 'inline-flex',
                alignItems: 'center',
                zIndex: '10000',
                position: 'relative',
                background: theme => theme.custom.c48,
            }}
        >
            <Text variant="h6" color="text-white" children={name} className="inline-block mr-[3px] uppercase" />
            <CloseIcon sx={{ cursor: 'pointer', color: theme => theme.palette.primary.main }} onClick={onClick} />
        </Box>
    );
};

interface CharactersDropableAreaProps {
    id_1: string;
    id_2: string;
    characters: string[];
    setCharactersAll: Dispatch<SetStateAction<string[]>>;
}

export const CharactersDropableArea = (props: CharactersDropableAreaProps) => {
    const { id_1, id_2, setCharactersAll, characters } = props;
    const { t } = useTranslation();
    const [importModal, setImportModal] = useAtom(importModalAtom);
    const characters_union = importModal.characters_union;

    const [characters_1, setCharacters1] = useState<string>('');
    const [characters_2, setCharacters2] = useState<string[]>([]);

    function handleDragEnd(event: DragEndEvent) {
        const { active, over } = event;
        const activeCharacter = characters.find(character => character === active.id);

        if (over && over.id === `characters-${id_1}`) {
            setCharactersAll(items =>
                items
                    .filter(item => item !== active.id)
                    .concat(characters_1)
                    .filter(BooleanFilter),
            );
            activeCharacter && setCharacters1(activeCharacter);
        }

        if (over && over.id === `characters-${id_2}` && activeCharacter) {
            setCharactersAll(items => items.filter(item => item !== active.id));
            setCharacters2(prev => [...prev, activeCharacter]);
        }
    }

    useDndMonitor({
        onDragEnd(event) {
            handleDragEnd(event);
        },
    });

    const approved = Boolean(characters_1.length && characters_2.length);

    useEffect(() => {
        if (approved) {
            let charactersObject = {};
            for (const character of characters_2) {
                charactersObject = {
                    ...charactersObject,
                    [character]: characters_1,
                };
            }
            setImportModal(prev => ({
                ...prev,
                characters_union: {
                    ...prev.characters_union,
                    ...charactersObject,
                },
            }));
        }
    }, [characters_1, characters_2, setImportModal, approved]);

    const clearCharacters = () => {
        setCharacters1('');
        setCharacters2([]);
        setCharactersAll(items => items.concat(characters_1).concat(characters_2).filter(BooleanFilter));
        removeCharacters(characters_2);
    };

    const removeCharacters = (names: string[]) => {
        const charactersObject = { ...characters_union };
        for (const name of names) {
            delete charactersObject[name];
        }
        setImportModal(prev => ({
            ...prev,
            characters_union: Object.values(charactersObject).length ? charactersObject : null,
        }));
    };

    return (
        <Grid
            gap="20px"
            container
            sx={{
                padding: '16px 20px',
                borderRadius: '16px',
                background: theme => theme.palette.primary.main,
            }}
        >
            <DroppableCharacters item md={3.4}>
                <div className="flex flex-wrap w-full gap-[5px] h-full items-start relative">
                    {characters_1 ? (
                        <CharacterItem
                            onClick={() => {
                                setCharacters1('');
                                removeCharacters(characters_2);
                                setCharactersAll(items => items.filter(character => character !== characters_1).concat(characters_1));
                            }}
                            key={characters_1}
                            name={characters_1}
                        />
                    ) : null}

                    <Typography
                        variant="body1"
                        sx={{
                            textAlign: 'center',
                            color: theme => theme.palette.info.contrastText,
                        }}
                        lineHeight="1.25"
                        children={t('import.character-move')}
                    />

                    <Droppable id={`characters-${id_1}`} />
                </div>
            </DroppableCharacters>

            <DroppableCharacters item md={8.2}>
                <div className="flex flex-wrap w-full gap-[5px] h-full items-start relative">
                    {characters_2.length
                        ? characters_2.map(item => (
                              <CharacterItem
                                  onClick={() => {
                                      removeCharacters([item]);
                                      setCharactersAll(items => items.concat(item));
                                      setCharacters2(prev => prev.filter(prev => prev !== item));
                                  }}
                                  key={item}
                                  name={item}
                              />
                          ))
                        : null}

                    <Typography
                        variant="body1"
                        sx={{
                            textAlign: 'center',
                            color: theme => theme.palette.info.contrastText,
                        }}
                        lineHeight="1.25"
                        children={t('import.characters-move')}
                    />

                    <Droppable id={`characters-${id_2}`} />
                </div>
            </DroppableCharacters>

            <div className="flex flex-wrap w-full justify-end">
                <Button
                    className="w-[152px] border-none text-primary bg-c40 hover:bg-c40"
                    variant="contained"
                    children={t('buttons.reset')}
                    onClick={clearCharacters}
                />
            </div>
        </Grid>
    );
};
